import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, BellRing, Check, Search, ShoppingBag, Smartphone, Monitor } from "lucide-react";
import { MEDIA } from "../mock";

const Label = ({ children }) => <p className="eyebrow text-[#7d7d72] leading-none">{children}</p>;

const Bars = ({ values, active = 3, h = "h-14" }) => (
  <div className={`mt-auto flex items-end gap-1 ${h}`}>
    {values.map((v, i) => (
      <span
        key={i}
        className={`flex-1 rounded-[2px] ${i >= values.length - active ? "bg-[#d8ff00]" : "bg-white/12"}`}
        style={{ height: `${v}%` }}
      />
    ))}
  </div>
);

const Row = ({ k, v, lime }) => (
  <div className="flex items-center justify-between text-xs gap-2">
    <span className="text-[#b9b9ae] truncate">{k}</span>
    <span className={lime ? "text-[#d8ff00] font-semibold shrink-0" : "text-white shrink-0"}>{v}</span>
  </div>
);

const Meter = ({ k, v, w }) => (
  <div>
    <div className="flex justify-between text-xs">
      <span className="text-[#b9b9ae]">{k}</span>
      <span className="text-white">{v}</span>
    </div>
    <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
      <span className="block h-full bg-[#d8ff00]" style={{ width: `${w}%` }} />
    </div>
  </div>
);

const Big = ({ value, sub }) => (
  <>
    <p className="text-[28px] xl:text-[34px] font-medium text-white mt-2 tracking-[-0.035em] leading-none">{value}</p>
    <p className="text-[11px] text-[#7d7d72] mt-2">{sub}</p>
  </>
);

const PANELS = [
  {
    body: (
      <>
        <Label>Obrat e-shopu</Label>
        <Big value="412 880 Kč" sub="↑ 34 % vs. minulý měsíc" />
        <Bars values={[26, 38, 31, 52, 44, 68, 88]} />
      </>
    ),
  },
  {
    lime: true,
    body: (
      <div className="flex flex-col h-full justify-between">
        <p className="eyebrow text-[#0e0e0d]/55 leading-none">Průměrně</p>
        <div>
          <p className="text-[46px] xl:text-[56px] font-medium text-[#0e0e0d] tracking-[-0.045em] leading-none">21</p>
          <p className="text-[#0e0e0d]/70 text-[13px] mt-2 leading-snug">dní od zadání po spuštění webu</p>
        </div>
      </div>
    ),
  },
  {
    body: (
      <>
        <Label>Nové poptávky</Label>
        <div className="mt-4 space-y-3">
          {[
            ["JN", "Jan Novák", "firemní web"],
            ["PK", "Petra Kučerová", "e-shop"],
            ["MD", "Martin Dvořák", "redesign"],
          ].map(([i, n, t]) => (
            <div key={n} className="flex items-center gap-2.5">
              <span className="grid place-items-center w-7 h-7 rounded-full bg-white/8 text-[10px] text-white/70 shrink-0">
                {i}
              </span>
              <div className="min-w-0">
                <p className="text-[13px] text-white truncate leading-tight">{n}</p>
                <p className="text-[11px] text-[#7d7d72] truncate">{t}</p>
              </div>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    body: (
      <>
        <Label>Core Web Vitals</Label>
        <div className="mt-4 space-y-3">
          <Meter k="LCP" v="0,7 s" w={92} />
          <Meter k="CLS" v="0,01" w={97} />
          <Meter k="INP" v="38 ms" w={88} />
        </div>
      </>
    ),
  },
  {
    body: (
      <>
        <Label>Návštěvnost</Label>
        <Big value="12 480" sub="návštěv za posledních 30 dní" />
        <Bars values={[40, 52, 46, 61, 58, 70, 66, 82, 91]} active={4} />
      </>
    ),
  },
  {
    body: (
      <>
        <div className="flex items-center gap-2">
          <Search size={13} className="text-[#d8ff00]" />
          <Label>Pozice v Google</Label>
        </div>
        <div className="mt-4 space-y-2.5">
          <Row k="tvorba webů praha" v="1." lime />
          <Row k="web na míru" v="3." lime />
          <Row k="redesign e-shopu" v="4." lime />
          <Row k="landing page cena" v="6." lime />
        </div>
      </>
    ),
  },
  {
    body: (
      <div className="flex flex-col h-full justify-between">
        <Label>PageSpeed</Label>
        <div className="relative mx-auto w-[86px] h-[86px]">
          <svg viewBox="0 0 36 36" className="w-[86px] h-[86px] -rotate-90">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
            <circle
              cx="18"
              cy="18"
              r="15.5"
              fill="none"
              stroke="#d8ff00"
              strokeWidth="3"
              strokeDasharray="97.4"
              strokeDashoffset="4"
              strokeLinecap="round"
            />
          </svg>
          <span className="absolute inset-0 grid place-items-center text-lg font-medium text-white">99</span>
        </div>
        <p className="text-[11px] text-[#7d7d72] text-center">ze 100 bodů na mobilu</p>
      </div>
    ),
  },
  {
    body: (
      <>
        <Label>Konverzní poměr</Label>
        <Big value="4,8 %" sub="z návštěvy na poptávku" />
        <Bars values={[18, 24, 21, 33, 30, 44, 58, 76]} active={4} />
      </>
    ),
  },
  {
    body: (
      <div className="flex flex-col h-full justify-center gap-4">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-8 h-8 rounded-full bg-white/8 text-[#d8ff00] shrink-0">
            <BellRing size={15} />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] text-white truncate leading-tight">Nová poptávka</p>
            <p className="text-[11px] text-[#7d7d72]">právě teď</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center w-8 h-8 rounded-full bg-[#d8ff00] text-[#0e0e0d] shrink-0">
            <ShoppingBag size={15} />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] text-white truncate leading-tight">Objednávka #1284</p>
            <p className="text-[11px] text-[#7d7d72]">2 490 Kč</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    body: (
      <>
        <Label>Péče o web</Label>
        <div className="mt-4 space-y-2.5">
          {["Dostupnost 100 %", "Záloha dnes 03:00", "Aktualizace hotové", "SSL v pořádku"].map((t) => (
            <div key={t} className="flex items-center gap-2.5">
              <span className="grid place-items-center w-[18px] h-[18px] rounded-full bg-[#d8ff00] text-[#0e0e0d] shrink-0">
                <Check size={10} strokeWidth={3.5} />
              </span>
              <p className="text-[12px] text-[#b9b9ae]">{t}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    body: (
      <div className="flex flex-col h-full justify-between">
        <Label>Hodnocení klientů</Label>
        <p className="text-white text-[13px] leading-relaxed">
          „Nový web nám za tři měsíce zdvojnásobil počet poptávek.“
        </p>
        <div className="flex items-center gap-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="w-2.5 h-2.5 rounded-[2px] bg-[#d8ff00]" />
          ))}
        </div>
      </div>
    ),
  },
  {
    body: (
      <>
        <Label>Odkud chodí lidé</Label>
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2.5">
            <Smartphone size={15} className="text-[#d8ff00]" />
            <Meter k="Mobil" v="68 %" w={68} />
          </div>
          <div className="flex items-center gap-2.5">
            <Monitor size={15} className="text-[#7d7d72]" />
            <Meter k="Desktop" v="32 %" w={32} />
          </div>
        </div>
      </>
    ),
  },
  {
    body: (
      <>
        <Label>Projekty v realizaci</Label>
        <div className="mt-4 space-y-3">
          <Meter k="Pekárna Zrno" v="80 %" w={80} />
          <Meter k="Studio Květ" v="45 %" w={45} />
          <Meter k="KrálFit" v="20 %" w={20} />
        </div>
      </>
    ),
  },
  {
    lime: true,
    body: (
      <div className="flex flex-col h-full justify-between">
        <p className="eyebrow text-[#0e0e0d]/55 leading-none">Od roku 2019</p>
        <div>
          <p className="text-[40px] xl:text-[48px] font-medium text-[#0e0e0d] tracking-[-0.045em] leading-none">40+</p>
          <p className="text-[#0e0e0d]/70 text-[13px] mt-2 leading-snug">spuštěných webů a e-shopů</p>
        </div>
      </div>
    ),
  },
  {
    body: (
      <>
        <Label>Formuláře</Label>
        <Big value="96" sub="odeslaných poptávek / měsíc" />
        <div className="mt-auto space-y-2.5">
          <Row k="Kontaktní formulář" v="61" />
          <Row k="Poptávka e-shopu" v="24" />
          <Row k="Newsletter" v="11" />
        </div>
      </>
    ),
  },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const scale = useTransform(scrollYProgress, [0, 0.42], [0.5, 1]);
  const radius = useTransform(scrollYProgress, [0, 0.42], ["34px", "0px"]);
  const panelsScale = useTransform(scrollYProgress, [0, 0.42], [1, 1.35]);
  const panelsOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const introOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const veil = useTransform(scrollYProgress, [0, 0.42], [0.3, 0.55]);
  const headOpacity = useTransform(scrollYProgress, [0.38, 0.58], [0, 1]);
  const headY = useTransform(scrollYProgress, [0.38, 0.62], [46, 0]);
  const footOpacity = useTransform(scrollYProgress, [0.5, 0.68], [0, 1]);

  const go = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} id="top" className="relative h-[230vh] bg-[#090908]" data-testid="hero-section">
      <div className="sticky top-0 h-screen overflow-hidden isolate">
        {/* Tiled dashboard panels bleeding off every edge (Teya-style intro) */}
        <motion.div
          style={{ scale: panelsScale, opacity: panelsOpacity }}
          className="absolute -inset-y-[4%] -inset-x-[7%] hidden md:grid grid-cols-5 grid-rows-3 gap-3.5 will-change-transform"
        >
          {PANELS.map((p, i) => (
            <div
              key={i}
              className={`p-5 overflow-hidden flex flex-col rounded-[18px] ${p.lime ? "bg-[#d8ff00]" : "widget"}`}
            >
              {p.body}
            </div>
          ))}
        </motion.div>

        {/* Video card that zooms to full bleed */}
        <motion.div
          style={{ scale, borderRadius: radius }}
          className="absolute inset-0 overflow-hidden z-10 will-change-transform"
          data-testid="hero-video-card"
        >
          <video
            className="absolute inset-0 w-full h-full object-cover"
            poster={MEDIA.heroPoster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            <source src={MEDIA.heroVideoWebm} type="video/webm" />
            <source src={MEDIA.heroVideo} type="video/mp4" />
          </video>
          <motion.div style={{ opacity: veil }} className="absolute inset-0 bg-[#090908]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#090908]/45 via-transparent to-[#090908]/80" />
        </motion.div>

        {/* Logo pinned top-centre during the intro, like Teya */}
        <motion.div
          style={{ opacity: introOpacity }}
          className="absolute top-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-[#090908]/70 backdrop-blur-md border border-white/10 pointer-events-none"
        >
          <span className="grid place-items-center w-8 h-8 rounded-[10px] bg-[#d8ff00] text-[#0e0e0d] font-bold text-[17px]">
            s
          </span>
          <span className="text-[21px] font-semibold text-white tracking-[-0.03em]">scaleone.cz</span>
        </motion.div>

        {/* Caption on the small card */}
        <motion.div
          style={{ opacity: introOpacity }}
          className="absolute inset-0 z-20 flex items-center justify-center px-10 text-center pointer-events-none"
        >
          <p className="display text-white text-2xl md:text-[30px] lg:text-[36px] max-w-xl">
            Jedno studio, které se postará o celý váš web — od návrhu až po výsledky
          </p>
        </motion.div>

        <motion.div
          style={{ opacity: introOpacity }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 text-white/55"
        >
          <span className="eyebrow">Sjeďte dolů</span>
          <ChevronDown size={18} className="nudge" />
        </motion.div>

        {/* Big brush headline */}
        <motion.div
          style={{ opacity: headOpacity, y: headY }}
          className="absolute inset-0 z-20 flex items-center justify-center px-6 pointer-events-none"
        >
          <h1 className="script text-[#d8ff00] text-center text-[19vw] sm:text-[15vw] lg:text-[11.5vw] leading-[0.92] drop-shadow-[0_10px_40px_rgba(0,0,0,0.5)]">
            Weby, co
            <br />
            prodávají.
          </h1>
        </motion.div>

        <motion.div style={{ opacity: footOpacity }} className="absolute bottom-10 left-0 right-0 z-30 container-x">
          <p className="text-white/90 text-base md:text-lg max-w-lg leading-relaxed">
            Navrhujeme a stavíme rychlé weby na míru, které vypadají skvěle, budují důvěru a přivádějí zákazníky.
          </p>
          <button onClick={() => go("#kontakt")} data-testid="hero-cta" className="btn-lime px-7 py-4 text-base mt-7">
            Chci nový web <ArrowRight size={18} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
